import PropTypes from 'prop-types';
import React, { Component } from 'react';
import $ from 'jquery';
import 'jquery-contextmenu';
import moment from 'moment-timezone';
import map from 'lodash/map';
import compact from 'lodash/compact';
import { EventEmitter } from 'events';
import { canUserUseBlockTime, canUserBookAppointments } from '../../../../common/helpers/users';
import {
  prepareAppointment,
  eventRender,
  eventAfterRender,
  eventClick,
  dayClick,
  prepareBlockTime,
  selectTime,
  canShowAppointmentOnCalendar,
} from '../../../../common/helpers/fullCalendar';
import { USER_TYPES } from '../../../../auth/constants';
import confirmModal from '../../../../common/confirmModal';

class CalendarWidget extends Component {
  componentDidMount() {
    const { appointments, loadedDate, blockedTimes } = this.props;
    this.initiateCalendar(appointments, loadedDate, blockedTimes);
  }

  componentDidUpdate() {
    // fullCalendar does not have a re-render feature yet (https://github.com/fullcalendar/fullcalendar/issues/2709)
    // so we have to destroy it and re-create it each time
    $(this.calendar).fullCalendar('destroy');
    this.initiateCalendar(this.props.appointments, this.props.loadedDate, this.props.blockedTimes);
  }

  componentWillUnmount() {
    $(this.calendar).fullCalendar('destroy');
  }

  initiateCalendar(appointments, loadedDate, blockedTimes) {
    const { timezone, barber, calendarWorkDay, deleteTime, user, minInterval } = this.props;
    const prepareCalendarAppointment = appointment => prepareAppointment(appointment, timezone, minInterval, user);
    let appointmentsArray = [];
    if (appointments && appointments.length) {
      appointmentsArray = appointments
        .map(prepareCalendarAppointment)
        .filter(canShowAppointmentOnCalendar);
    }

    const eventCalendarClick =
        event => eventClick(event, appointmentsArray, this.props.permissions, this.props.fetchAppointment);
    const dayCalendarClick = (...args) => dayClick(...args, barber.id, this.props.permissions);
    const selectWithBarber =
      (...args) => selectTime(...args, barber, user.kind === USER_TYPES.barber, this.props.permissions);
    const backgroundEvents = compact(map(blockedTimes, day => prepareBlockTime(day, timezone, barber)));
    const events = [
      ...appointmentsArray,
      ...backgroundEvents,
    ];

    const hours = this.props.workday ? {
      dow: [0, 1, 2, 3, 4, 5, 6],
      ...this.props.workday,
    } : { dow: [] };

    const canUseBlockTime = canUserUseBlockTime(this.props.permissions);
    const canBookAppointment = canUserBookAppointments(this.props.permissions);
    const options = {
      defaultView: 'agendaDay',
      allDaySlot: false,
      events,
      header: '',
      timezone,
      minTime: calendarWorkDay.start,
      maxTime: calendarWorkDay.end,
      businessHours: hours,
      slotEventOverlap: false,
      slotDuration: `00:${(`0${minInterval}`.slice(-2))}:00`,
      slotLabelInterval: '01:00:00',
      slotLabelFormat: 'h(:mm)a',
      snapDuration: `00:${(`0${minInterval}`.slice(-2))}:00`,
      timeFormat: 'h(:mm)a',
      defaultDate: loadedDate,
      nowIndicator: true,
      now: moment.tz(timezone).format(),
      eventRender,
      eventAfterRender,
      displayEventEnd: true,
      eventClick: eventCalendarClick,
      dayClick: dayCalendarClick,
      selectable: true,
      select: selectWithBarber,
      selectMinDistance: 10,
      selectAllow: selectInfo => selectInfo.end.diff(selectInfo.start, 'minutes') >= minInterval * 2,
      viewDestroy: () => {
        $.contextMenu('destroy');
      },
      eventAfterAllRender: () => {
        // Prevent context menu not reappearing.
        $('div.fc-bgevent:not(.fc-nonbusiness)').removeClass('context-menu-active');
        $.contextMenu({
          selector: 'div.fc-bgevent:not(.fc-nonbusiness)',
          trigger: 'left',
          build($trigger) {
            const dayOff = $trigger.data('dayOff');
            return {
              callback(key) {
                switch (key) {
                  case 'edit': {
                    EventEmitter.prototype.emit('open-block-time-modal', {
                      dayOff: $(this).data('dayOff'),
                      hideBarber: user.kind === USER_TYPES.barber,
                    });
                    break;
                  }
                  case 'delete': {
                    if (dayOff.isRecurring) {
                      confirmModal(
                        {
                          title: '',
                          message: 'Would you like to delete all future occurrences of this blocked times?',
                          type: 'yesnoreverse',
                          confirmButtonText: 'Yes',
                          negativeButtonText: 'No',
                        },
                        () => deleteTime(dayOff.barberId, dayOff.id, true),
                        () => deleteTime(dayOff.barberId, dayOff.id),
                        () => null
                      );
                    } else {
                      deleteTime(dayOff.barberId, dayOff.id);
                    }
                    break;
                  }
                  case 'add':
                    dayCalendarClick($(this).data('date'), null, { type: 'day' });
                    break;
                  default:
                    break;
                }
              },
              items: {
                edit: {
                  name: 'Edit block time',
                  visible: () => canUseBlockTime,
                },
                delete: {
                  name: 'Delete block time',
                  visible: () => canUseBlockTime,
                },
                add: {
                  name: 'Add appointment',
                  visible: () => canBookAppointment,
                },
              },
            };
          },
        });
      },
    };

    $(this.calendar).fullCalendar(options);
  }

  render() {
    const { calendarClass, barber } = this.props;
    return (
      <div
        className={calendarClass}
        data-barber={barber.id}
        data-automation-id={`container:barberCalendarColumn:${barber.id}`}
      >
        <div className="c-calendar-dot-line" />
        <div ref={(el) => { this.calendar = el; }} />
      </div>
    );
  }
}

CalendarWidget.propTypes = {
  loadedDate: PropTypes.string.isRequired,
  calendarClass: PropTypes.string.isRequired,
  barber: PropTypes.object.isRequired,
  appointments: PropTypes.arrayOf(PropTypes.object),
  timezone: PropTypes.string.isRequired,
  calendarWorkDay: PropTypes.object.isRequired,
  deleteTime: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  blockedTimes: PropTypes.array.isRequired,
  workday: PropTypes.object.isRequired,
  fetchAppointment: PropTypes.func.isRequired,
  minInterval: PropTypes.number.isRequired,
};
CalendarWidget.defaultProps = {
  appointments: [],
};

export default CalendarWidget;
